/**
 * Pagination Component
 * Used in record list pages (announcements, press releases, corporate governance)
 */
import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import styled from '@emotion/styled'
import { LangKey } from '../types'

interface PaginationProps {
  langKey: LangKey
  perPage: number
  isFirst: boolean
  isLast: boolean
  currentPage: number
  setCurrentPage: (page: number) => void
  noRecord: boolean
  totalCount: number
}

const DEFAULT_COLOR = '#ff6637'

const PaginationWrapper = styled.div`
  max-width: 1200px;
  margin: 1rem auto 10rem;
  padding: 0 2rem;
  min-height: 30px;
  line-height: 30px;
  overflow: hidden;
  font-size: 14px;
  color: #666;

  a {
    color: #666;
  }

  span {
    margin-right: 2rem;
  }

  @media (max-width: 767px) {
    padding: 0 1.25rem;

    span {
      margin-right: 1rem;
    }
  }
`

const Pagination: React.FC<PaginationProps> = ({
  langKey,
  perPage,
  isFirst,
  isLast,
  currentPage,
  setCurrentPage,
  noRecord,
  totalCount,
}) => {
  const data = useStaticQuery(graphql`
    query PaginationQuery {
      sanitySiteSettings {
        color {
          hex
        }
      }
    }
  `)

  const primaryColor = data?.sanitySiteSettings?.color?.hex || DEFAULT_COLOR

  if (noRecord || totalCount === 0) return null

  const totalPages = Math.ceil(totalCount / perPage)
  const prevLabel = langKey === 'zh-hant' ? '上一頁' : 'Previous'
  const nextLabel = langKey === 'zh-hant' ? '下一頁' : 'Next'

  return (
    <PaginationWrapper>
      {isFirst ? (
        <span>{prevLabel}</span>
      ) : (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setCurrentPage(currentPage - 1)
            window.scrollTo(0, 0)
          }}
        >
          {prevLabel}
        </span>
      )}

      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <span
          key={page}
          style={{
            cursor: page === currentPage ? 'auto' : 'pointer',
            color: page === currentPage ? primaryColor : 'auto',
          }}
          onClick={() => {
            if (page !== currentPage) {
              setCurrentPage(page)
              window.scrollTo(0, 0)
            }
          }}
        >
          {page}
        </span>
      ))}

      {isLast ? (
        <span>{nextLabel}</span>
      ) : (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setCurrentPage(currentPage + 1)
            window.scrollTo(0, 0)
          }}
        >
          {nextLabel}
        </span>
      )}
    </PaginationWrapper>
  )
}

export default Pagination
